import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CreditCard, ArrowUpRight, AlertTriangle, Check, Zap } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGES = {
  starter: { color: "bg-slate-100 text-slate-700", label: "Starter" },
  growth:  { color: "bg-blue-100 text-blue-700",   label: "Growth" },
  scale:   { color: "bg-purple-100 text-purple-700", label: "Scale" },
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subStatus, setSubStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [orderUsage, setOrderUsage] = useState(null);
  const [interval, setInterval] = useState("month");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, plansRes, usageRes] = await Promise.all([
        axios.get(`${API}/billing/subscription-status`, { headers }),
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/order-usage`, { headers }),
      ]);
      setSubStatus(statusRes.data);
      setPlans(plansRes.data.plans);
      setOrderUsage(usageRes.data);
    } catch (err) {
      console.error("Error loading billing data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    if (sessionId) {
      pollCheckoutStatus(sessionId);
    } else if (canceled) {
      toast.info("Checkout cancelado");
    }
  }, [searchParams]);

  const pollCheckoutStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 5) {
      toast.error("No se pudo verificar el pago. Recarga la página.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Accept "paid" (normal subscription) or "no_payment_required" (trial)
      if (res.data.payment_status === "paid" || res.data.payment_status === "no_payment_required") {
        const isTrial = res.data.payment_status === "no_payment_required";
        toast.success(isTrial ? "Prueba iniciada correctamente — 14 días gratis" : "Suscripción activada correctamente");
        fetchData();
        navigate("/settings/billing", { replace: true });
      } else {
        setTimeout(() => pollCheckoutStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollCheckoutStatus(sessionId, attempt + 1), 2000);
    }
  };

  const handleCheckout = async (planKey) => {
    setCheckoutLoading(planKey);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/billing/create-checkout-session`,
        { plan_key: planKey, interval, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear sesión de pago");
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/billing/create-portal-session`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al abrir portal de facturación");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" />
      </div>
    );
  }

  const isActive = subStatus?.subscription_status === "active";
  const isTrialing = subStatus?.subscription_status === "trialing";
  const isGrandfathered = subStatus?.is_grandfathered;
  const currentPlan = subStatus?.plan_key;
  const badge = currentPlan ? PLAN_BADGES[currentPlan] : null;

  // Trial days remaining
  const trialDaysLeft = (() => {
    if (!isTrialing || !subStatus?.trial_end) return null;
    const ms = new Date(subStatus.trial_end) - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="billing-page">
      {/* Current status */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Facturación</h1>
        <p className="text-slate-500">Gestiona tu suscripción y uso del servicio</p>
      </div>

      {/* Subscription status card */}
      <Card data-testid="billing-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Estado de suscripción
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isActive ? "bg-green-100 text-green-700" :
              isTrialing ? "bg-amber-100 text-amber-800" :
              isGrandfathered ? "bg-amber-100 text-amber-700" :
              "bg-red-100 text-red-700"
            }`} data-testid="subscription-status-badge">
              {isActive ? "Activa" : isTrialing ? "Prueba activa" : isGrandfathered ? "Período de gracia" : "Inactiva"}
            </span>
            {badge && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
                {badge.label}
              </span>
            )}
          </div>

          {/* Trial active banner */}
          {isTrialing && trialDaysLeft !== null && (
            <div
              className={`border rounded-lg p-4 text-sm ${
                trialDaysLeft <= 3
                  ? "bg-red-50 border-red-200 text-red-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
              data-testid="trial-active-notice"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    Prueba activa — quedan {trialDaysLeft} {trialDaysLeft === 1 ? "día" : "días"}
                  </p>
                  <p className="mt-1">
                    Tu período de prueba termina el{" "}
                    <strong>{new Date(subStatus.trial_end).toLocaleDateString("es-ES")}</strong>.
                    {subStatus?.plan && (
                      <> Tu tarjeta se cargará <strong>{subStatus.plan.price}€/mes</strong> a partir de esa fecha.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isGrandfathered && !isActive && !isTrialing && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800" data-testid="grandfather-notice">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Período de gracia activo</p>
                  <p>Tu acceso es temporal hasta el {new Date(subStatus.grandfather_until).toLocaleDateString("es-ES")}. Selecciona un plan para continuar usando la plataforma.</p>
                </div>
              </div>
            </div>
          )}

          {subStatus?.cancel_at_period_end && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <p className="font-medium">Cancelación programada</p>
              <p>Tu suscripción se cancelará al final del período actual ({new Date(subStatus.current_period_end).toLocaleDateString("es-ES")}).</p>
            </div>
          )}

          {(isActive || isTrialing) && (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handlePortal}
                disabled={portalLoading}
                data-testid="manage-subscription-btn"
              >
                {portalLoading ? "Abriendo..." : isTrialing ? "Gestionar o cancelar prueba" : "Gestionar suscripción"}
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Usage */}
      {orderUsage && (
        <Card data-testid="order-usage-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Uso de pedidos ({orderUsage.year})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Pedidos procesados</span>
                <span className="font-semibold text-slate-900">
                  {orderUsage.order_count.toLocaleString("es-ES")}
                  {orderUsage.max_orders_per_year && (
                    <span className="text-slate-500 font-normal"> / {orderUsage.max_orders_per_year.toLocaleString("es-ES")}</span>
                  )}
                </span>
              </div>
              {orderUsage.max_orders_per_year && (
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      orderUsage.usage_percentage >= 90 ? "bg-red-500" :
                      orderUsage.usage_percentage >= 70 ? "bg-amber-500" :
                      "bg-green-500"
                    }`}
                    style={{ width: `${Math.min(orderUsage.usage_percentage, 100)}%` }}
                    data-testid="order-usage-bar"
                  />
                </div>
              )}
              {orderUsage.limit_reached && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Has alcanzado el límite de pedidos de tu plan. Mejora tu plan para continuar importando.</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan selection */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900">Planes</h2>
          <div className="flex items-center bg-slate-100 rounded-lg p-1" data-testid="billing-interval-toggle">
            <button
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                interval === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
              onClick={() => setInterval("month")}
            >
              Mensual
            </button>
            <button
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                interval === "year" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
              onClick={() => setInterval("year")}
            >
              Anual <span className="text-green-600 text-xs ml-1">-20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.plan_key && (isActive || isTrialing);
            const price = interval === "year" ? plan.annual_price : plan.price;
            const perMonth = interval === "year" ? (plan.annual_price / 12).toFixed(0) : plan.price;

            return (
              <Card
                key={plan.plan_key}
                className={`relative overflow-hidden ${
                  plan.plan_key === "growth" ? "border-blue-300 shadow-md" : ""
                } ${isCurrent ? "ring-2 ring-green-400" : ""}`}
                data-testid={`plan-card-${plan.plan_key}`}
              >
                {plan.plan_key === "growth" && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Popular
                  </div>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-slate-900">{perMonth}€</span>
                    <span className="text-slate-500 text-sm">/mes</span>
                    {interval === "year" && (
                      <p className="text-xs text-slate-500 mt-1">
                        Facturado {price}€/año
                      </p>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{plan.features.max_orders_per_year?.toLocaleString("es-ES")} pedidos/año</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{plan.features.max_shops} tienda</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{plan.features.max_users} usuario{plan.features.max_users > 1 ? "s" : ""}</span>
                    </li>
                    {plan.features.integrations_enabled?.map((int_name) => (
                      <li key={int_name} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="capitalize">{int_name.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                    {plan.features.csv_export && (
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Exportación CSV</span>
                      </li>
                    )}
                    {plan.features.ai_insights && (
                      <li className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        <span>AI Insights</span>
                      </li>
                    )}
                  </ul>

                  {isCurrent ? (
                    <Button variant="outline" disabled className="w-full" data-testid={`plan-current-${plan.plan_key}`}>
                      Plan actual
                    </Button>
                  ) : (
                    <Button
                      className={`w-full ${
                        plan.plan_key === "growth" ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"
                      } text-white`}
                      onClick={() => handleCheckout(plan.plan_key)}
                      disabled={!!checkoutLoading}
                      data-testid={`plan-select-${plan.plan_key}`}
                    >
                      {checkoutLoading === plan.plan_key ? "Redirigiendo..." :
                       (isActive || isTrialing) ? "Cambiar plan" :
                       subStatus?.trial_eligible ? "Comenzar prueba gratuita" : "Suscribirse"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
